import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import { TSubscriptionType, TUserVipTimeType } from "../../../Types";
import {
  SUBSCRIPTION_MAX_MESSAGE_KEYS,
  SUBSCRIPTION_MAX_VALUES,
  SUBSCRIPTION_MIN_MESSAGE_KEYS,
  SUBSCRIPTION_MIN_VALUES,
  ZERO_VALUE
} from "../../../constants/input-entries-constants";
import getCorePopUpStore from "../../core/core-popup-store";
import { isNullOrUndefined, isPositiveNumber } from "et3-type-guards";
import getUserStore from "../../user-store";
import i18n, { Ti18nKeys } from "../../../constants/i18n";
import getCoreUIStore from "../../core/core-ui-store";
import { apiCallGiveUserSubscription } from "../../../api/scsr-api/api-call-give-user-subscription";

class GiveSubscriptionsStore {
  vipDaysInput = observable.box<number>(-1);
  vipHoursInput = observable.box<number>(-1);
  vipMinInput = observable.box<number>(-1);
  plusDaysInput = observable.box<number>(-1);
  plusHoursInput = observable.box<number>(-1);
  plusMinInput = observable.box<number>(-1);
  khattabatDaysInput = observable.box<number>(-1);
  khattabatHoursInput = observable.box<number>(-1);
  khattabatMinInput = observable.box<number>(-1);
  silverDaysInput = observable.box<number>(-1);
  silverHoursInput = observable.box<number>(-1);
  silverMinInput = observable.box<number>(-1);
  goldDaysInput = observable.box<number>(-1);
  goldHoursInput = observable.box<number>(-1);
  goldMinInput = observable.box<number>(-1);
  platinumDaysInput = observable.box<number>(-1);
  platinumHoursInput = observable.box<number>(-1);
  platinumMinInput = observable.box<number>(-1);
  advancedDaysInput = observable.box<number>(-1);
  advancedHoursInput = observable.box<number>(-1);
  advancedMinInput = observable.box<number>(-1);
  blackDaysInput = observable.box<number>(-1);
  blackHoursInput = observable.box<number>(-1);
  blackMinInput = observable.box<number>(-1);
  placeholders: {
    [key in TSubscriptionType]: {
      [timeType in TUserVipTimeType]: Ti18nKeys;
    };
  } = {
    VIP: {
      DAYS: "VIP_DAYS",
      HOURS: "VIP_HOURS",
      MINUTES: "VIP_MIN"
    },
    PLUS: {
      DAYS: "PLUS_DAYS",
      HOURS: "PLUS_HOURS",
      MINUTES: "PLUS_MIN"
    },
    ALKHATTABAT: {
      DAYS: "KHATTABAT_DAYS",
      HOURS: "KHATTABAT_HOURS",
      MINUTES: "KHATTABAT_MIN"
    },
    PLATINUM: {
      DAYS: "PLATINUM_DAYS",
      HOURS: "PLATINUM_HOURS",
      MINUTES: "PLATINUM_MIN"
    },
    SILVER: {
      DAYS: "SILVER_DAYS",
      HOURS: "SILVER_HOURS",
      MINUTES: "SILVER_MIN"
    },
    GOLD: {
      DAYS: "GOLD_DAYS",
      HOURS: "GOLD_HOURS",
      MINUTES: "GOLD_MIN"
      },
      ADVANCED: {
        DAYS: "ADVANCED_DAYS",
        HOURS: "ADVANCED_HOURS",
        MINUTES: "ADVANCED_MIN"
    },
    BLACK: {
      DAYS: "BLACK_DAYS",
      HOURS: "BLACK_HOURS",
      MINUTES: "BLACK_MIN"
    }
  };
  buttonText: {
    [key in TSubscriptionType]: Ti18nKeys;
  } = {
    VIP: "MAKE_VIP",
    PLUS: "MAKE_PLUS",
    ALKHATTABAT: "MAKE_KHATTABAT",
    SILVER: "MAKE_SILVER",
    GOLD: "MAKE_GOLD",
      PLATINUM: "MAKE_PLATINUM",
      ADVANCED: "MAKE_ADVANCED",
    BLACK: "MAKE_BLACK"
  };

  getPlaceholdersAndButtonText = (subscriptionType: TSubscriptionType) => {
    return {
      placeholders: this.placeholders[subscriptionType],
      buttonText: this.buttonText[subscriptionType]
    };
  };

  getSubscriptionInputType = (
    vipTimeType: TUserVipTimeType,
    subscriptionType: TSubscriptionType
  ) => {
    const subscriptionMap = {
      VIP: {
        DAYS: this.vipDaysInput,
        HOURS: this.vipHoursInput,
        MINUTES: this.vipMinInput
      },
      PLUS: {
        DAYS: this.plusDaysInput,
        HOURS: this.plusHoursInput,
        MINUTES: this.plusMinInput
      },
      ALKHATTABAT: {
        DAYS: this.khattabatDaysInput,
        HOURS: this.khattabatHoursInput,
        MINUTES: this.khattabatMinInput
      },
      SILVER: {
        DAYS: this.silverDaysInput,
        HOURS: this.silverHoursInput,
        MINUTES: this.silverMinInput
      },
      GOLD: {
        DAYS: this.goldDaysInput,
        HOURS: this.goldHoursInput,
        MINUTES: this.goldMinInput
      },
      PLATINUM: {
        DAYS: this.platinumDaysInput,
        HOURS: this.platinumHoursInput,
        MINUTES: this.platinumMinInput
      },
      ADVANCED: {
        DAYS: this.advancedDaysInput,
        HOURS: this.advancedHoursInput,
        MINUTES: this.advancedMinInput
      },
      BLACK: {
        DAYS: this.blackDaysInput,
        HOURS: this.blackHoursInput,
        MINUTES: this.blackMinInput
      }
    };

    return subscriptionMap[subscriptionType][vipTimeType];
  };

  getSubscriptionInputTime = (
    subscriptionTimeType: TUserVipTimeType,
    subscriptionType: TSubscriptionType
  ) => {
    return this.getSubscriptionInputType(
      subscriptionTimeType,
      subscriptionType
    ).get();
  };

  setSubscriptionTime = (
    value: number,
    subscriptionTimeType: TUserVipTimeType,
    subscriptionType: TSubscriptionType
  ) => {
    runInAction(() => {
      const subscriptionInput = this.getSubscriptionInputType(
        subscriptionTimeType,
        subscriptionType
      );
      subscriptionInput.set(value);
    });
  };

  validateSubscriptionTime = (
    subscriptionTimeType: TUserVipTimeType,
    subscriptionType: TSubscriptionType
  ) => {
    const subscriptionInput = this.getSubscriptionInputType(
      subscriptionTimeType,
      subscriptionType
    );
    const currentValue = subscriptionInput.get();
    const maxValue =
      SUBSCRIPTION_MAX_VALUES[subscriptionType][subscriptionTimeType];
    const minValue =
      SUBSCRIPTION_MIN_VALUES[subscriptionType]?.[subscriptionTimeType];

    if (isNullOrUndefined(maxValue) && isNullOrUndefined(minValue)) {
      getCorePopUpStore().showAlertMessage("INVALID_INPUT");
      return;
    }

    if (currentValue > maxValue) {
      runInAction(() => subscriptionInput.set(maxValue));
      getCorePopUpStore().showHelperMessage(
        SUBSCRIPTION_MAX_MESSAGE_KEYS[subscriptionType][subscriptionTimeType]
      );
    } else if (
      !isNullOrUndefined(minValue) &&
      isPositiveNumber(currentValue) &&
      currentValue < minValue
    ) {
      runInAction(() => subscriptionInput.set(minValue));
      getCorePopUpStore().showHelperMessage(
        SUBSCRIPTION_MIN_MESSAGE_KEYS[subscriptionType]![subscriptionTimeType]!
      );
    }
  };

  isSubscriptionButtonEnabled = (
    subscriptionType: TSubscriptionType
  ): boolean => {
    const days = getGiveSubscriptionsStore().getSubscriptionInputTime(
      "DAYS",
      subscriptionType
    );
    const hours = getGiveSubscriptionsStore().getSubscriptionInputTime(
      "HOURS",
      subscriptionType
    );
    const minutes = getGiveSubscriptionsStore().getSubscriptionInputTime(
      "MINUTES",
      subscriptionType
    );

    const isTimeValid =
      isPositiveNumber(days) ||
      isPositiveNumber(hours) ||
      isPositiveNumber(minutes);

    const isTieredSubscription =
      subscriptionType === "SILVER" ||
      subscriptionType === "GOLD" ||
      subscriptionType === "PLATINUM" ||
      subscriptionType === "ADVANCED" ||
      subscriptionType === "BLACK";

    if (isTieredSubscription && !isPositiveNumber(days)) {
      return false;
    }

    return isTimeValid && isPositiveNumber(getUserStore().userIdInput.get());
  };

  makeUser = async (userId: number, subscriptionType: TSubscriptionType) => {
    getCorePopUpStore().closeConfirmationPopUps();
    await this.addSubscriptionsDaysAndHoursOnDatabase(userId, subscriptionType);
    this.getSubscriptionInputType("DAYS", subscriptionType).set(-1);
    this.getSubscriptionInputType("HOURS", subscriptionType).set(-1);
    this.getSubscriptionInputType("MINUTES", subscriptionType).set(-1);
  };

  getExtraDataText = (subscriptionType: TSubscriptionType) => {
    const days = isPositiveNumber(
      this.getSubscriptionInputTime("DAYS", subscriptionType)
    )
      ? this.getSubscriptionInputTime("DAYS", subscriptionType)
      : ZERO_VALUE;
    const hours = isPositiveNumber(
      this.getSubscriptionInputTime("HOURS", subscriptionType)
    )
      ? this.getSubscriptionInputTime("HOURS", subscriptionType)
      : ZERO_VALUE;
    const minutes = isPositiveNumber(
      this.getSubscriptionInputTime("MINUTES", subscriptionType)
    )
      ? this.getSubscriptionInputTime("MINUTES", subscriptionType)
      : ZERO_VALUE;

    return i18n.getWithArrayExtraData("SUBSCRIPTION_TIME_CONFIRMATION", [
      `${days}`,
      `${hours}`,
      `${minutes}`
    ]);
  };

  addSubscriptionsDaysAndHoursOnDatabase = async (
    userId: number,
    subscriptionType: TSubscriptionType
  ) => {
    let successMessage: Ti18nKeys;
    switch (subscriptionType) {
      case "VIP":
        successMessage = "USER_VIP_ADDED_SUCCESSFULLY";
        break;
      case "PLUS":
        successMessage = "USER_PLUS_ADDED_SUCCESSFULLY";
        break;
      case "ALKHATTABAT":
        successMessage = "USER_KHATTABAT_ADDED_SUCCESSFULLY";
        break;
      case "SILVER":
        successMessage = "USER_SILVER_ADDED_SUCCESSFULLY";
        break;
      case "GOLD":
        successMessage = "USER_GOLD_ADDED_SUCCESSFULLY";
        break;
      case "PLATINUM":
        successMessage = "USER_PLATINUM_ADDED_SUCCESSFULLY";
        break;
      case "ADVANCED":
        successMessage = "USER_ADVANCED_ADDED_SUCCESSFULLY";
        break;
      case "BLACK":
        successMessage = "USER_BLACK_ADDED_SUCCESSFULLY";
        break;
    }

    const days = this.getSubscriptionInputTime("DAYS", subscriptionType);
    const hours = this.getSubscriptionInputTime("HOURS", subscriptionType);
    const min = this.getSubscriptionInputTime("MINUTES", subscriptionType);

    if (
      !isPositiveNumber(days) &&
      !isPositiveNumber(hours) &&
      !isPositiveNumber(min)
    ) {
      getCorePopUpStore().showAlertMessage("ERROR");
      return;
    }

    getCoreUIStore().setIsLoading("SCSR_GIVE_SUBSCRIPTIONS", true);
    const { status, code } = await apiCallGiveUserSubscription({
      userId,
      subscriptionType,
      days,
      hours,
      min
    });
    getCoreUIStore().setIsLoading("SCSR_GIVE_SUBSCRIPTIONS", false);

    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage(successMessage!);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}

const getGiveSubscriptionsStore = memoize(
  () => new GiveSubscriptionsStore(),
  () => 1
);
export default getGiveSubscriptionsStore;
